/**
 * 签名认证（支持国密SM3和标准SHA-256）
 * 
 * 算法选择：
 * - 企业版优先使用SM3国密算法
 * - 标准版使用SHA-256算法
 * - 自动降级：sm-crypto库不可用时降级到SHA-256
 */
import * as smCrypto from 'sm-crypto';

// sm-crypto 导出的 sm3 函数（CommonJS 模块的命名导出）
const sm3 = smCrypto.sm3 || smCrypto.default?.sm3;

export class Auth {
  constructor(appKey, appSecret, options = {}) {
    this.appKey = appKey;
    this.appSecret = appSecret;
    // 配置：useSM3 = true 使用SM3，false 使用SHA-256
    this.useSM3 = options.useSM3 !== false; // 默认启用SM3
  }

  /**
   * 设置算法配置
   * @param {boolean} useSM3 - 是否使用SM3算法
   */
  setAlgorithm(useSM3) {
    this.useSM3 = useSM3;
    console.log('[Auth] 算法已切换:', useSM3 ? 'SM3' : 'SHA-256');
  }

  /**
   * 生成认证头
   * @param {string} method - HTTP 方法（大写）
   * @param {string} path - 请求路径
   * @param {Object} body - 请求体（可选）
   * @returns {Object} 认证头对象
   */
  async generateHeaders(method, path, body) {
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const nonce = this._generateNonce();
    const bodyStr = body ? JSON.stringify(body) : '';
    const bodyHash = body ? await this._hash(bodyStr) : '';

    const signContent = `${this.appKey}${timestamp}${nonce}${method.toUpperCase()}${path}${bodyHash}`;
    const signature = await this._hmac(this.appSecret, signContent);

    // 调试日志
    console.log('[Auth] 签名计算详情:');
    console.log('  算法:', this.useSM3 ? 'SM3' : 'SHA-256');
    console.log('  bodyStr:', bodyStr);
    console.log('  bodyHash:', bodyHash);
    console.log('  signContent:', signContent);
    console.log('  signature:', signature);

    return {
      'X-App-Key': this.appKey,
      'X-Timestamp': timestamp,
      'X-Nonce': nonce,
      'X-Signature': signature,
      'X-Algorithm': this.useSM3 ? 'SM3' : 'SHA-256',
      'Content-Type': 'application/json'
    };
  }

  /**
   * 生成信令连接认证参数
   */
  async generateSignAuth(transferId) {
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const nonce = this._generateNonce();
    const path = '/ws/signaling';
    const signContent = `${this.appKey}${timestamp}${nonce}GET${path}`;
    const sign = await this._hmac(this.appSecret, signContent);

    return {
      appKey: this.appKey,
      timestamp,
      nonce,
      sign,
      transferId,
      algorithm: this.useSM3 ? 'SM3' : 'SHA-256'  // 告知服务端签名算法
    };
  }

  _generateNonce(length = 16) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * HMAC签名（支持HMAC-SM3和HMAC-SHA256）
   */
  async _hmac(key, message) {
    if (this.useSM3) {
      return this._hmacSm3(key, message);
    }
    return this._hmacSha256(key, message);
  }

  /**
   * HMAC-SM3签名（国密算法）
   */
  _hmacSm3(key, message) {
    try {
      // HMAC算法：H(K XOR opad, H(K XOR ipad, text))
      const keyBytes = new TextEncoder().encode(key);
      const msgBytes = new TextEncoder().encode(message);

      const ipad = new Uint8Array(64);
      const opad = new Uint8Array(64);

      // 初始化ipad和opad
      for (let i = 0; i < 64; i++) {
        ipad[i] = 0x36;
        opad[i] = 0x5c;
      }

      // K XOR ipad和K XOR opad
      const keyLen = Math.min(keyBytes.length, 64);
      for (let i = 0; i < keyLen; i++) {
        ipad[i] ^= keyBytes[i];
        opad[i] ^= keyBytes[i];
      }

      // 计算内部哈希：H(K XOR ipad, data)
      const innerData = this._concatUint8Arrays([ipad, msgBytes]);
      const innerHash = sm3(Array.from(innerData));

      // 计算外部哈希：H(K XOR opad, innerHash)
      const outerData = this._concatUint8Arrays([opad, this._hexToBytes(innerHash)]);
      const outerHash = sm3(Array.from(outerData));

      return outerHash;
    } catch (e) {
      console.warn('[Auth] SM3签名失败，降级到SHA-256:', e.message);
      return this._hmacSha256(key, message);
    }
  }

  /**
   * HMAC-SHA256签名（标准算法）
   */
  async _hmacSha256(key, message) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(key);
    const msgData = encoder.encode(message);
    const cryptoKey = await crypto.subtle.importKey('raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, msgData);
    return Array.from(new Uint8Array(signature)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * 哈希计算（支持SM3和SHA-256）
   */
  async _hash(string) {
    if (this.useSM3) {
      return this._sm3(string);
    }
    return this._sha256(string);
  }

  /**
   * SM3哈希（国密算法）
   */
  _sm3(string) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(string);
      return sm3(Array.from(data));
    } catch (e) {
      console.warn('[Auth] SM3哈希失败，降级到SHA-256:', e.message);
      return this._sha256(string);
    }
  }

  /**
   * SHA-256哈希（标准算法）
   */
  async _sha256(string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(string);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * 字节数组转十六进制字符串
   */
  _bytesToHex(bytes) {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * 十六进制字符串转字节数组
   */
  _hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  }

  /**
   * 合并多个Uint8Array
   */
  _concatUint8Arrays(arrays) {
    let totalLen = 0;
    for (const arr of arrays) totalLen += arr.length;
    const result = new Uint8Array(totalLen);
    let offset = 0;
    for (const arr of arrays) {
      result.set(arr, offset);
      offset += arr.length;
    }
    return result;
  }
}